import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useTargetUseCases, UseCase } from "../../../lib/api";

/**
 * TargetUseCaseSelector Component
 *
 * Lazy-loads use cases from SERVER_CONFIG endpoint when dropdown is opened
 */
interface TargetUseCaseSelectorProps {
  className?: string;
  label?: string;
}

export function TargetUseCaseSelector({
  className,
  label = "Target Use Case"
}: TargetUseCaseSelectorProps) {
  const {
    targetFacilityId,
    targetUseCaseId,
    setTargetUseCaseId,
    setTargetAuthorId
  } = useWorkflowStore();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  // Fetch use cases only after dropdown has been opened
  const {
    data: useCases = [],
    isLoading,
    error,
    refetch
  } = useTargetUseCases(targetFacilityId, hasOpened);

  // Reset dependent selections when use case changes
  useEffect(() => {
    setTargetAuthorId(null);
  }, [targetUseCaseId, setTargetAuthorId]);

  // Reset when facility changes
  useEffect(() => {
    setTargetUseCaseId(null);
    setHasOpened(false);
  }, [targetFacilityId, setTargetUseCaseId]);

  // Handle dropdown open - trigger fetch
  const handleOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    if (open && !hasOpened && targetFacilityId) {
      setHasOpened(true);
      refetch();
    }
  };

  // Handle use case selection
  const handleUseCaseChange = (value: string) => {
    setTargetUseCaseId(value);
  };

  const isDisabled = !targetFacilityId;

  const getPlaceholderText = () => {
    if (!targetFacilityId) return "Select target facility first";
    if (!hasOpened) return "Click to load use cases";
    return "Select target use case";
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="target-use-case">{label}</Label>
        <Select
          value={targetUseCaseId || ""}
          onValueChange={handleUseCaseChange}
          disabled={isDisabled}
          open={dropdownOpen}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger id="target-use-case">
            <SelectValue placeholder={getPlaceholderText()} />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading use cases...
              </SelectItem>
            ) : error ? (
              <SelectItem value="error" disabled>
                Error: {error instanceof Error ? error.message : "Failed to load use cases"}
              </SelectItem>
            ) : useCases.length === 0 ? (
              <SelectItem value="none" disabled>
                No use cases available
              </SelectItem>
            ) : (
              useCases.map((useCase: UseCase) => (
                <SelectItem key={useCase.id} value={useCase.id}>
                  {useCase.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}